/**
 * Monitoring Service
 * API calls for system monitoring data
 */

import { apiClient } from './apiClient';

export interface MetricData {
  timestamp: Date;
  value: number;
  labels?: Record<string, string>;
}

export interface Alert {
  id: string;
  name: string;
  condition: string;
  severity: 'critical' | 'warning' | 'info';
  status: 'active' | 'resolved';
  triggeredAt: Date;
  resolvedAt?: Date;
  message: string;
  source?: string;
  type?: string;
}

export interface UptimeCheck {
  id: string;
  name: string;
  url?: string;
  status: 'up' | 'down' | 'degraded';
  responseTime: number;
  lastChecked: Date;
  uptime: number;
  checksTotal?: number;
  checksSuccess?: number;
}

export interface SystemMetrics {
  cpu: {
    usage: number;
    trend: 'up' | 'down' | 'stable';
  };
  memory: {
    usage: number;
    total: number;
    trend: 'up' | 'down' | 'stable';
  };
  network: {
    requestsPerSecond: number;
    errorRate: number;
    avgLatency: number;
  };
  database: {
    connections: number;
    queryTime: number;
    writes: number;
    reads: number;
  };
  timestamp?: Date;
}

export interface SystemLog {
  id: string;
  timestamp: Date;
  severity: 'ERROR' | 'WARNING' | 'INFO' | 'DEBUG';
  message: string;
  resource?: string;
  metadata?: Record<string, any>;
}

export const monitoringService = {
  /**
   * Get current system metrics
   */
  async getSystemMetrics(): Promise<{ metrics: SystemMetrics; history: SystemMetrics[] }> {
    try {
      const response = await apiClient.get('/getSystemMetrics');
      return {
        metrics: response.data.metrics || {
          cpu: { usage: 0, trend: 'stable' },
          memory: { usage: 0, total: 4, trend: 'stable' },
          network: { requestsPerSecond: 0, errorRate: 0, avgLatency: 0 },
          database: { connections: 0, queryTime: 0, writes: 0, reads: 0 },
        },
        history: response.data.history || [],
      };
    } catch (error) {
      console.error('Error fetching system metrics:', error);
      // Return default metrics on error
      return {
        metrics: {
          cpu: { usage: 0, trend: 'stable' },
          memory: { usage: 0, total: 4, trend: 'stable' },
          network: { requestsPerSecond: 0, errorRate: 0, avgLatency: 0 },
          database: { connections: 0, queryTime: 0, writes: 0, reads: 0 },
        },
        history: [],
      };
    }
  },

  /**
   * Get uptime checks status
   */
  async getUptimeChecks(): Promise<UptimeCheck[]> {
    try {
      const response = await apiClient.get('/getUptimeChecks');
      return response.data.uptimeChecks || [];
    } catch (error) {
      console.error('Error fetching uptime checks:', error);
      return [];
    }
  },

  /**
   * Get monitoring alerts
   */
  async getAlerts(filters?: {
    status?: 'active' | 'resolved' | 'all';
    severity?: 'critical' | 'warning' | 'info' | 'all';
    limit?: number;
  }): Promise<{ alerts: Alert[]; stats: { active: number; resolved: number } }> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.severity) params.append('severity', filters.severity);
      if (filters?.limit) params.append('limit', String(filters.limit));

      const response = await apiClient.get(`/getMonitoringAlerts?${params.toString()}`);
      return {
        alerts: response.data.alerts || [],
        stats: response.data.stats || { active: 0, resolved: 0 },
      };
    } catch (error) {
      console.error('Error fetching alerts:', error);
      return { alerts: [], stats: { active: 0, resolved: 0 } };
    }
  },

  /**
   * Get active alerts only
   */
  async getActiveAlerts(): Promise<Alert[]> {
    const result = await this.getAlerts({ status: 'active' });
    return result.alerts;
  },

  /**
   * Get recent alerts (last 7 days)
   */
  async getRecentAlerts(limit: number = 50): Promise<Alert[]> {
    const result = await this.getAlerts({ limit });
    return result.alerts;
  },

  /**
   * Resolve an alert
   */
  async resolveAlert(alertId: string, resolution?: string): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.post('/resolveAlert', { alertId, resolution });
      return { success: response.data.success };
    } catch (error) {
      console.error('Error resolving alert:', error);
      return { success: false };
    }
  },

  /**
   * Get system logs
   */
  async getLogs(filters?: {
    severity?: 'ERROR' | 'WARNING' | 'INFO' | 'DEBUG' | 'all';
    limit?: number;
  }): Promise<SystemLog[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.severity && filters.severity !== 'all') params.append('severity', filters.severity);
      if (filters?.limit) params.append('limit', String(filters.limit));

      const response = await apiClient.get(`/getSystemLogs?${params.toString()}`);
      return response.data.logs || [];
    } catch (error) {
      console.error('Error fetching system logs:', error);
      return [];
    }
  },

  /**
   * Get metric time series data (from history)
   */
  async getMetricTimeSeries(
    metricType: 'cpu' | 'memory' | 'requests' | 'errors' | 'latency',
    _duration: '1h' | '24h' | '7d' | '30d' = '24h'
  ): Promise<MetricData[]> {
    try {
      const { history } = await this.getSystemMetrics();
      
      return history.map((m: any) => {
        let value = 0;
        switch (metricType) {
          case 'cpu':
            value = m.cpu?.usage || 0;
            break;
          case 'memory':
            value = m.memory?.usage || 0;
            break;
          case 'requests':
            value = m.network?.requestsPerSecond || 0;
            break;
          case 'errors':
            value = m.network?.errorRate || 0;
            break;
          case 'latency':
            value = m.network?.avgLatency || 0;
            break;
        }
        return {
          timestamp: new Date(m.timestamp),
          value,
        };
      });
    } catch (error) {
      console.error('Error fetching metric time series:', error);
      return [];
    }
  },

  /**
   * Get monitoring dashboard URL (Google Cloud Console)
   */
  getMonitoringDashboardUrl(): string {
    return 'https://console.cloud.google.com/monitoring/dashboards?project=un-dollar-rentflow-dev';
  },

  /**
   * Get logs explorer URL (Google Cloud Console)
   */
  getLogsExplorerUrl(): string {
    return 'https://console.cloud.google.com/logs/query?project=un-dollar-rentflow-dev';
  },

  /**
   * Get trace explorer URL (Google Cloud Console)
   */
  getTraceExplorerUrl(): string {
    return 'https://console.cloud.google.com/traces/list?project=un-dollar-rentflow-dev';
  },
};

export default monitoringService;
